import { css, Stack, Typography, useTheme } from '@mui/material'
import { CustomTypeOptions } from 'i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { match } from 'ts-pattern'
import FacebookLogo from 'assets/icons/facebook.svg'
import InstagramLogo from 'assets/icons/instagram.svg'
import LinkedinLogo from 'assets/icons/linkedin.svg'
import { AuthModalContext } from 'components/Auth/AuthModalContext'
import { MilesRepublicLogo } from 'components/Branding/MilesRepublicLogo'
import { useCookieConsentApi } from 'components/Cookie/CookieConsentContext'
import {
  FakeLink,
  FooterContainer,
  FooterContent
} from 'components/Layout/Footer.style'
import { LegacyLinkButton } from 'components/LegacyLinkButton'
import { LinkButtonVariant } from 'components/LinkButtonVariant'
import { NewFlexContainer } from 'components/common/common.style'
import { recordKeys } from 'helpers/records'
import { unifiedColors } from 'styles/colors'
import { Theme, themeUtils } from 'styles/theme'

type FooterLinkTitle = 'enterprise' | 'runners' | 'organizers' | 'timers'

interface FooterLink {
  label: Exclude<
    keyof CustomTypeOptions['resources']['home'],
    'head' | 'footer' | 'filters'
  >
  link: string
  isExternal?: boolean
  showModal?: 'login' | 'cookie'
  languages?: string[]
}

const tagList: FooterLink[] = [
  { label: 'roadRaces', link: '/running/' },
  { label: 'trails', link: '/trail/' },
  { label: 'triathlons', link: '/triathlon/' },
  { label: 'funRaces', link: '/fun/' },
  { label: 'walks', link: '/marche/' }
]

const footerLinks: Record<FooterLinkTitle, readonly FooterLink[]> = {
  enterprise: [
    { label: 'contactUs', link: '/contact' },
    { label: 'aboutUs', link: '/about-us' },
    {
      label: 'becomeShareholder',
      link: '/mag/levee-de-fonds-communautaire'
    },
    {
      label: 'weAreHiring',
      link: 'https://milesrepublic.notion.site/Rejoins-Miles-Republic-1bc1b1dc2c52469799cd557352c5f146',
      isExternal: true
    },
    { label: 'legalNotice', link: '/legal-notice' },
    { label: 'cookieModal', link: '/', showModal: 'cookie' }
  ],
  runners: [
    { label: 'runnerLogin', link: '/', showModal: 'login' },
    { label: 'runnerNewsletter', link: '/newsletter' },
    { label: 'milesMag', link: '/mag', languages: ['fr'] },
    { label: 'termsOfUse', link: '/terms-of-use' },
    { label: 'privacyPolicy', link: '/privacy-policy' },
    { label: 'termsOfSale', link: '/terms-of-sale' },
    { label: 'allRaces', link: '/les-courses' },
    { label: 'racesByRegion', link: '/les-courses-par-region' },
    { label: 'siteMap', link: '/mag/plan-disciplines', languages: ['fr'] },
    { label: 'disciplines', link: '/mag/plan', languages: ['fr'] }
  ],
  organizers: [
    { label: 'organizerSpace', link: '/dashboard', isExternal: true },
    { label: 'ourServices', link: '/organizers/nos-services' },
    { label: 'timerQuote', link: '/organizers/devis-chronometrage' },
    { label: 'helpCenter', link: '/organizers/help-center' }
  ],
  timers: [{ label: 'timers', link: '/timers/nos-services' }]
}

export default function Footer() {
  const theme: Theme = useTheme()
  const { setModalParams } = useContext(AuthModalContext)
  const { showPreferences } = useCookieConsentApi()
  const { i18n } = useTranslation()
  const router = useRouter()
  const { status } = useSession()
  const isLoggedIn = status === 'authenticated'
  const { t } = useTranslation('home')

  return (
    <FooterContainer>
      <FooterContent>
        <NewFlexContainer
          justifyContent="space-between"
          alignItems="flex-start"
          css={css`
            ${themeUtils.maxWidth.md} {
              display: grid;
              padding: 0 16px;
              gap: 16px;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              ${themeUtils.maxWidth.md} {
                width: 130px;
              }
            `}
          >
            <Link prefetch={false} href={'/'} passHref aria-label="home logo">
              <MilesRepublicLogo
                color={unifiedColors.label.default}
                css={css`
                  width: 180px;
                `}
              />
            </Link>
          </div>
          <NewFlexContainer
            flexDirection="column"
            gap="32px"
            css={css`
              ${themeUtils.maxWidth.md} {
                gap: 20px;
              }
            `}
          >
            <NewFlexContainer
              css={css`
                ${themeUtils.maxWidth.sm} {
                  flex-direction: column;
                }
              `}
            >
              <Typography
                variant="h2"
                css={css`
                  ${themeUtils.minWidth.sm} {
                    &:after {
                      content: ',';
                      padding-right: 8px;
                    }
                  }
                `}
              >
                {t('title')}
              </Typography>
              <Typography variant="h2">{t('subtitle')}</Typography>
            </NewFlexContainer>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                ${themeUtils.maxWidth.md} {
                  display: grid;
                  gap: 16px;
                  grid-template-columns: repeat(3, minmax(0, 1fr));
                }
                ${themeUtils.maxWidth.sm} {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }
              `}
            >
              {tagList.map((tagContent, id) => {
                return (
                  <div
                    key={`tag_${id}_${tagContent.label}`}
                    css={css`
                      display: flex;
                      margin-right: 16px;
                      ${themeUtils.maxWidth.md} {
                        flex-direction: column;
                        text-align: center;
                        margin: 0;
                      }
                    `}
                  >
                    <LegacyLinkButton
                      href={tagContent.link}
                      variation={LinkButtonVariant.OUTLINED}
                      variationColor={unifiedColors.label.default}
                      hoverVariationColor={unifiedColors.button.primary.hovered}
                    >
                      {t(tagContent.label)}
                    </LegacyLinkButton>
                  </div>
                )
              })}
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                gap: 32px;
                flex-wrap: wrap;
                ${themeUtils.maxWidth.md} {
                  display: grid;
                  grid-template-columns: repeat(3, 1fr);
                  width: 100%;
                  gap: 16px;
                }
                ${themeUtils.maxWidth.sm} {
                  grid-template-columns: repeat(2, 1fr);
                }
                & a {
                  ${themeUtils.bodyXSmall}
                  color: ${unifiedColors.label.default};
                  &:hover {
                    ${themeUtils.minWidth.md} {
                      margin-bottom: 16px;
                      text-decoration-thickness: 1.5px;
                    }
                  }
                }
              `}
            >
              {recordKeys(footerLinks).map((title, id) => {
                return (
                  <Stack key={`highlight_group_${id}`} gap={0.5}>
                    <span
                      css={css`
                        ${css({
                          ...(theme.typography.h5 as React.CSSProperties)
                        })}
                        font-family: ${themeUtils.bodyFont};
                        color: ${unifiedColors.label.default};
                        font-weight: 700;
                      `}
                    >
                      {t(title)}
                    </span>
                    {footerLinks[title].map((linkElem) =>
                      !linkElem.languages ||
                      linkElem.languages.includes(i18n.language) ? (
                        <div key={`highlight_link_${linkElem.label}`}>
                          {match(linkElem)
                            .with({ showModal: 'login' }, () => {
                              if (isLoggedIn) {
                                return (
                                  <Link
                                    href="/account"
                                    passHref
                                    prefetch={false}
                                  >
                                    {t('account')}
                                  </Link>
                                )
                              }

                              return (
                                <FakeLink
                                  aria-label={linkElem.label}
                                  onClick={() =>
                                    setModalParams({
                                      redirectUrl: router.asPath,
                                      state: 'SIGNIN'
                                    })
                                  }
                                >
                                  {t(linkElem.label)}
                                </FakeLink>
                              )
                            })
                            .with({ showModal: 'cookie' }, () => {
                              return (
                                <FakeLink
                                  aria-label={linkElem.label}
                                  onClick={showPreferences}
                                >
                                  {t(linkElem.label)}
                                </FakeLink>
                              )
                            })
                            .with({ isExternal: true }, () => (
                              <a
                                href={linkElem.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={linkElem.label}
                              >
                                {t(linkElem.label)}
                              </a>
                            ))
                            .otherwise(() => (
                              <Link
                                href={linkElem.link}
                                passHref
                                prefetch={false}
                              >
                                {t(linkElem.label)}
                              </Link>
                            ))}
                        </div>
                      ) : null
                    )}
                  </Stack>
                )
              })}
            </div>
          </NewFlexContainer>
        </NewFlexContainer>
        <NewFlexContainer
          justifyContent="space-between"
          padding="18px 24px"
          css={css`
            border-radius: 32px;
            background-color: ${unifiedColors.button.tertiary.default};
            ${themeUtils.maxWidth.md} {
              padding: 12px 16px;
            }
          `}
        >
          <Typography
            variant="h2"
            css={css`
              ${themeUtils.maxWidth.md} {
                font-size: 24px;
                line-height: 1.2;
                width: 45%;
              }
            `}
          >
            {t('footer.socialNetwork')}
          </Typography>
          <div
            css={css`
              & svg {
                height: 32px;
                width: 32px;
                margin-right: 16px;
              }
              & path {
                fill: ${unifiedColors.label.default};
              }
              display: flex;
              flex-direction: row;
              a {
                display: flex;
                align-items: center;
              }
            `}
          >
            <a
              href="https://www.facebook.com/PeyceFRA"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="facebook"
            >
              <FacebookLogo />
            </a>
            <a
              href="https://www.instagram.com/milesrepublic/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="instagram"
            >
              <InstagramLogo />
            </a>
            <a
              href="https://www.linkedin.com/company/peyce/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="linkedin"
            >
              <LinkedinLogo />
            </a>
          </div>
        </NewFlexContainer>
      </FooterContent>
    </FooterContainer>
  )
}
